// docs/116 CI11: the guestbook open on its lectern. Two parchment pages a spread, newest entries
// first, a page turned with a rustle; the last page has room to write, as one of my heroes.
import { useMemo, useState } from 'react';
import { Button } from '../../../../components';
import { playSfx } from '../../../../../core/audio';
import { serverNow } from '../../../../../core/time';
import { GUESTBOOK_MAX, type GuestbookEntry, type InnData } from '../types';
import type { VillageLook } from '../../types';
import { HeroPick, Scene, firstPayer, useOpenSound } from './common';

const PER_PAGE = 4;

export function ago(atMs: number, now: number): string {
  const s = Math.max(0, (now - atMs) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return m === 1 ? 'a minute ago' : `${m} minutes ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return h === 1 ? 'an hour ago' : `${h} hours ago`;
  const d = Math.floor(h / 24);
  if (d < 14) return d === 1 ? 'yesterday' : `${d} days ago`;
  const w = Math.floor(d / 7);
  return w < 9 ? `${w} weeks ago` : 'long ago';
}

type Page = { kind: 'entries'; entries: GuestbookEntry[] } | { kind: 'first' } | { kind: 'write' };

function Entry({ e, now }: { e: GuestbookEntry; now: number }) {
  return (
    <li className={`fk-innp__entry ${e.mine ? 'is-mine' : ''}`} data-testid="guestbook-entry">
      <p className="fk-innp__entry-text">{e.text}</p>
      <span className="fk-innp__entry-sign"><b>{e.heroName}</b>{e.who && <> · written by {e.who}</>} · {ago(e.atMs, now)}</span>
    </li>
  );
}

export function Guestbook({ data, look }: { data: InnData; look: VillageLook }): React.JSX.Element {
  useOpenSound('inn.page', 0.55);
  const pages = useMemo<Page[]>(() => {
    const ps: Page[] = [];
    if (!data.guestbook.length) ps.push({ kind: 'first' });
    for (let i = 0; i < data.guestbook.length; i += PER_PAGE) ps.push({ kind: 'entries', entries: data.guestbook.slice(i, i + PER_PAGE) });
    ps.push({ kind: 'write' });
    if (ps.length % 2) ps.push({ kind: 'entries', entries: [] });
    return ps;
  }, [data.guestbook]);
  const spreads = Math.ceil(pages.length / 2);
  const [spread, setSpread] = useState(0);
  const at = Math.min(spread, spreads - 1);
  const turn = (to: number) => { if (to === at || to < 0 || to >= spreads) return; playSfx('inn.page', { gain: 0.6 }); setSpread(to); };
  const writeAt = Math.floor(pages.findIndex((p) => p.kind === 'write') / 2);

  const [hero, setHero] = useState(() => firstPayer(data.payers)?.id);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [bad, setBad] = useState<string>();
  const signer = data.payers.find((p) => p.id === hero) ?? data.payers[0];
  const now = serverNow();
  const sign = async () => {
    const t = text.trim();
    if (!signer || !t) return;
    setBusy(true);
    playSfx('inn.quill', { gain: 0.7 });
    const ok = await data.act.sign(signer.id, t.slice(0, GUESTBOOK_MAX));
    setBusy(false);
    if (ok) { setText(''); setBad(undefined); window.setTimeout(() => { playSfx('inn.page', { gain: 0.5 }); setSpread(0); }, 450); }
    else { playSfx('ui.error'); setBad('The ink blots. Try again in a moment.'); }
  };

  const page = (p: Page | undefined, side: 'left' | 'right', n: number) => (
    <div className={`fk-innp__page fk-innp__page--${side}`}>
      {p?.kind === 'first' && (
        <div className="fk-innp__first">
          <b className="fk-innp__book-title">The Guestbook</b>
          <span className="fk-innp__first-sub">of the inn at {look.name || 'the village'}</span>
          <p>The pages are clean and the ink is fresh. Nobody has written here yet. Be the first: say who you are, and something nice about the stew.</p>
        </div>
      )}
      {p?.kind === 'entries' && (p.entries.length ? <ul className="fk-innp__entries">{p.entries.map((e) => <Entry key={e.id.toString()} e={e} now={now} />)}</ul> : <span className="fk-innp__blank" aria-hidden="true">❦</span>)}
      {p?.kind === 'write' && (
        <div className="fk-innp__write">
          <b className="fk-innp__write-head">Leave a line</b>
          <HeroPick payers={data.payers} value={signer?.id} onChange={setHero} label="Signed by" testid="guestbook-hero" />
          <textarea className="fk-innp__ink" value={text} maxLength={GUESTBOOK_MAX} rows={4} placeholder="Good ale, a warm fire, and the cat is a menace…"
            onChange={(e) => setText(e.target.value.slice(0, GUESTBOOK_MAX))} onKeyDown={(e) => e.stopPropagation()} aria-label="What to write in the guestbook" data-testid="guestbook-text" disabled={!signer} />
          <span className="fk-innp__write-foot">
            <span className={`fk-innp__count ${text.length >= GUESTBOOK_MAX ? 'is-full' : ''}`}>{text.length}/{GUESTBOOK_MAX}</span>
            <Button small primary disabled={busy || !signer || !text.trim()} onClick={() => void sign()} data-testid="guestbook-sign">Sign the book</Button>
          </span>
          {bad && <span className="fk-innp__said is-bad" role="status">{bad}</span>}
          <span className="fk-innp__faint">Everyone in this World can read it, any time they stop in.</span>
        </div>
      )}
      <span className="fk-innp__folio" aria-hidden="true">{n + 1}</span>
    </div>
  );
  return (
    <Scene name="guestbook" label="The guestbook" className="fk-innp__scene--book"
      caption={<>{data.guestbook.length ? `${data.guestbook.length} ${data.guestbook.length === 1 ? 'entry' : 'entries'}, newest first` : 'A new book'}</>}>
      <div className="fk-innp__book" data-spread={at}>
        <div key={at} className="fk-innp__spread">
          {page(pages[at * 2], 'left', at * 2)}
          {page(pages[at * 2 + 1], 'right', at * 2 + 1)}
        </div>
        <span className="fk-innp__ribbon" aria-hidden="true" />
      </div>
      <div className="fk-innp__book-nav">
        <Button small variant="ghost" disabled={at === 0} onClick={() => turn(at - 1)} data-testid="guestbook-back">‹ Turn back</Button>
        <span className="fk-innp__faint">pages {at * 2 + 1}–{at * 2 + 2} of {pages.length}</span>
        {at !== writeAt && <Button small variant="ghost" onClick={() => turn(writeAt)} data-testid="guestbook-to-write">✎ Write in it</Button>}
        <Button small variant="ghost" disabled={at >= spreads - 1} onClick={() => turn(at + 1)} data-testid="guestbook-next">Turn on ›</Button>
      </div>
    </Scene>
  );
}
